import { useEffect, useRef, useState } from 'react';
import { User, X, Mail, Phone } from 'lucide-react';
import { GHOST_CUSTOMERS_EVENT, GhostCustomerCard, emitGhostChoice } from '../lib/assistant/customerLookup';

/**
 * "Ghost" candidate cards — when the AI's customer lookup finds several
 * possible matches, the screen dims and the candidates float as a centered
 * stack of blue-glow skeleton cards, like the AI's brain projecting the
 * options it's weighing. Tapping a card tells the AI "use this one"; tapping
 * the X tells it the operation was cancelled (both via emitGhostChoice).
 * The user can also just answer by voice or chat instead.
 */
export default function AssistantGhostMatches() {
  const [customers, setCustomers] = useState<GhostCustomerCard[]>([]);
  const [title, setTitle] = useState<string>('');
  const dismissTimer = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const list = Array.isArray(detail.customers) ? (detail.customers as GhostCustomerCard[]) : [];
      setCustomers(list.slice(0, 6));
      setTitle(typeof detail.title === 'string' ? detail.title : '');
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
      if (list.length > 0) {
        // Long enough to read + answer the AI, short enough to never feel stuck
        dismissTimer.current = window.setTimeout(() => setCustomers([]), 25_000);
      }
    };
    window.addEventListener(GHOST_CUSTOMERS_EVENT, handler);
    return () => {
      window.removeEventListener(GHOST_CUSTOMERS_EVENT, handler);
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    };
  }, []);

  if (customers.length === 0) return null;

  return (
    <div
      data-assistant-capture-ignore="true"
      className="fixed inset-0 z-[10050] flex flex-col items-center justify-center px-4"
    >
      <style>{`
        @keyframes assistantGhostDim {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes assistantGhostIn {
          from { opacity: 0; transform: translateY(14px) scale(0.94); filter: blur(5px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes assistantGhostFloat {
          from { transform: translateY(0); }
          to   { transform: translateY(-5px); }
        }
        @keyframes assistantGhostPulse {
          from { box-shadow: 0 0 14px rgba(59, 130, 246, 0.25), inset 0 0 10px rgba(59, 130, 246, 0.06); }
          to   { box-shadow: 0 0 26px rgba(59, 130, 246, 0.5), inset 0 0 14px rgba(59, 130, 246, 0.12); }
        }
      `}</style>

      {/* Dimmed backdrop — visual only, taps pass through */}
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px] pointer-events-none"
        style={{ animation: 'assistantGhostDim 250ms ease-out both' }}
      />

      {/* Header pill */}
      <div
        className="relative mb-4 flex items-center gap-2 pl-3.5 pr-1.5 py-1.5 rounded-full border border-blue-400/60 bg-white/90 backdrop-blur-md"
        style={{
          animation: 'assistantGhostIn 300ms ease-out both, assistantGhostPulse 2.4s ease-in-out infinite alternate',
        }}
      >
        <User className="h-4 w-4 text-blue-500" />
        <p className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">
          {title || 'Which one did you mean?'}
        </p>
        <button
          type="button"
          onClick={() => {
            setCustomers([]);
            emitGhostChoice({ kind: 'dismissed' });
          }}
          className="p-1 rounded-full text-blue-400 hover:text-blue-600 hover:bg-blue-100 transition-colors"
          aria-label="Cancel — none of these"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Centered stack of blue-glow skeleton cards */}
      <div className="relative flex flex-col items-center gap-3 w-full max-w-sm max-h-[62vh] overflow-y-auto py-1">
        {customers.map((c, i) => (
          <div
            key={c.id}
            className="w-full"
            style={{ animation: `assistantGhostIn 400ms ease-out ${120 + i * 110}ms both` }}
          >
            <button
              type="button"
              onClick={() => {
                setCustomers([]);
                emitGhostChoice({ kind: 'selected', customer: c });
              }}
              aria-label={`Use ${c.name}${c.email ? ` (${c.email})` : c.phone ? ` (${c.phone})` : ''}`}
              className="w-full text-left rounded-2xl border border-blue-400/60 bg-white/85 backdrop-blur-md px-4 py-3 cursor-pointer transition-transform duration-150 hover:scale-[1.02] hover:border-blue-500 hover:bg-white active:scale-[0.98]"
              style={{
                animation: `assistantGhostFloat ${2.8 + i * 0.4}s ease-in-out ${i * 200}ms infinite alternate, assistantGhostPulse 2.6s ease-in-out ${i * 300}ms infinite alternate`,
              }}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-400/50 bg-blue-50">
                  <User className="h-4 w-4 text-blue-500" />
                </span>
                <p className="text-[14px] font-bold text-gray-900 leading-tight truncate">{c.name}</p>
              </div>
              <div className="mt-2 flex flex-col gap-1 pl-[42px]">
                {c.email && (
                  <p className="flex items-center gap-1.5 text-[11.5px] text-gray-600 truncate">
                    <Mail className="h-3 w-3 shrink-0 text-blue-400" />
                    {c.email}
                  </p>
                )}
                {c.phone && (
                  <p className="flex items-center gap-1.5 text-[11.5px] text-gray-600">
                    <Phone className="h-3 w-3 shrink-0 text-blue-400" />
                    {c.phone}
                  </p>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>

      <p
        className="relative mt-4 text-[11px] text-blue-200/70"
        style={{ animation: 'assistantGhostIn 400ms ease-out 500ms both' }}
      >
        Tap the one you mean — or just tell the assistant. Tap ✕ to cancel.
      </p>
    </div>
  );
}
